import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  createToast,
  FormControl,
  List,
  ListItem,
  Modal,
  ModalHeader,
  Text,
  Toast,
  ToastButton,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { getCsrfToken } from 'query';

type Props = {
  reviewTitle: string;
  reviewId: string;
  deleteReviewFn?: (reviewId: string) => Promise<any>;
};

const deleteReview = (reviewId: string) => {
  return fetch(`/reviews/${reviewId}`, {
    method: 'DELETE',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
    },
  }).then((response: Response) => {
    if (response.redirected) {
      return response.url;
    } else {
      return Promise.reject();
    }
  });
};

export const DeleteReview = (props: Props) => {
  const { reviewTitle, reviewId, deleteReviewFn = deleteReview } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [warn, setWarn] = useState(false);

  return (
    <>
      <Button
        variant="solid"
        type="danger"
        onClick={() => {
          setModalOpen(true);
        }}
      >
        Delete Review
      </Button>
      <Modal isOpen={modalOpen} id="delete-review-modal">
        <Stack spacing={4} id="delete-review-description">
          <ModalHeader>Delete review?</ModalHeader>
          <Text size="md">
            You&apos;re about to delete the review{' '}
            <strong>&quot;{reviewTitle}&quot;.</strong>
          </Text>
          <Text component="div" size="md">
            Before continuing, please confirm that you understand: <br />
            <List>
              <ListItem>This review cannot be restored once deleted</ListItem>
              <ListItem>
                It will not re-credit your plan with an additional review
              </ListItem>
              <ListItem>All review team members will lose access</ListItem>
              <ListItem>
                All data and progress will be permanently deleted
              </ListItem>
            </List>
          </Text>
          <FormControl
            aria-live="assertive"
            error={
              warn
                ? ' Please confirm you understand the impact of deleting'
                : undefined
            }
          >
            <Checkbox
              onChange={(e) => {
                const val = e.target.checked;
                setChecked(val);
                setWarn(!val);
              }}
              aria-describedby="delete-review-description"
            >
              Yes, I understand and want to
              <strong>&nbsp;permanently delete review</strong>
            </Checkbox>
          </FormControl>

          <Text component="div">
            <Inline alignX="right" spacing={3}>
              <Button
                size="md"
                onClick={() => {
                  setModalOpen(false);
                  setWarn(false);
                }}
                variant="outline"
                type="neutral"
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                type="danger"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  if (!checked) {
                    setWarn(true);
                  } else {
                    const triggerElement = event.currentTarget;
                    deleteReviewFn(reviewId)
                      .then((url) => {
                        window.location.href = url as string;
                      })
                      .catch(() => {
                        createToast(
                          <Toast
                            className="saved-changes-toast"
                            type="danger"
                            focusOnAppear
                            restoreFocusTo={triggerElement}
                          >
                            Oops! Something went wrong when saving data
                            <ToastButton to={window.location.href}>
                              Reload
                            </ToastButton>
                          </Toast>
                        );
                      })
                      .finally(() => {
                        setModalOpen(false);
                      });
                  }
                }}
              >
                Delete Review
              </Button>
            </Inline>
          </Text>
        </Stack>
      </Modal>
    </>
  );
};
